import React, { useCallback, useEffect, useRef } from 'react';
import { X, Tag, AlertCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  OFFER_STATUSES,
  fmtDate,
  statusLabel,
} from '@/features/dashboard/data/claudeMockData';
import { CompanyBadge } from '@/features/dashboard/components/claude/shared';
import type { OfferCard } from '../interfaces/offer-card.interface';
import { buildCompanyForBadge } from '../utils/buildCompanyForBadge';
import { useOfferHtml } from '../hooks/useOfferHtml';

interface OfferCardModalProps {
  isOpen: boolean;
  offer: OfferCard | null;
  onClose: () => void;
}

/**
 * Modal placeholder pentru detalii ofertă: doar header cu ref + status +
 * companie + data emiterii (per spec). Footer cu Download/Cere booking/Close
 * a fost scos intenționat. Singura modalitate de a închide este `X` din
 * header sau click pe backdrop / tasta Escape.
 *
 * Conținutul real al modal-ului va fi proiectat ulterior.
 */
export const OfferCardModal: React.FC<OfferCardModalProps> = ({ isOpen, offer, onClose }) => {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';

  // Fetch HTML doar când modal-ul e deschis pe o ofertă concretă.
  const { data: html, isLoading: htmlLoading, error: htmlError } = useOfferHtml(
    isOpen && offer ? offer.id : null,
  );

  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Oferta e HTML cu lățime fixă (gândit pentru PDF/print) și depășea ecranul.
  // O randăm la dimensiunile ei native, apoi scalăm tot iframe-ul (ca un bloc)
  // cu `transform: scale(...)` astfel încât lățimea afișată să fie exact cât
  // containerul (bottom sheet-ul). Wrapper-ul cu `overflow: hidden` taie surplusul,
  // iar înălțimea lui o setăm la cea scalată ca să rezerve spațiul corect.
  const fitIframe = useCallback(() => {
    const iframe = iframeRef.current;
    const wrap = iframe?.parentElement;
    const doc = iframe?.contentDocument;
    if (!iframe || !wrap || !doc || !doc.documentElement) return;

    // Reset ca să măsurăm dimensiunile reale ale documentului.
    iframe.style.transform = 'none';
    iframe.style.width = '100%';
    iframe.style.height = 'auto';

    const naturalWidth =
      doc.documentElement.scrollWidth || doc.body?.scrollWidth || 0;
    if (!naturalWidth) return;

    // Fixăm iframe-ul pe lățimea nativă ca să obținem înălțimea corespunzătoare.
    iframe.style.width = `${naturalWidth}px`;
    const naturalHeight =
      doc.documentElement.scrollHeight || doc.body?.scrollHeight || 0;
    iframe.style.height = `${naturalHeight}px`;

    const containerWidth = wrap.clientWidth;
    const scale = containerWidth / naturalWidth;

    iframe.style.transformOrigin = 'top left';
    iframe.style.transform = `scale(${scale})`;
    wrap.style.height = `${Math.ceil(naturalHeight * scale)}px`;
  }, []);

  useEffect(() => {
    if (!html) return;
    // Refit după load (imagini/fonturi pot sosi târziu) și la resize/rotire.
    const t1 = setTimeout(fitIframe, 60);
    const t2 = setTimeout(fitIframe, 400);
    window.addEventListener('resize', fitIframe);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', fitIframe);
    };
  }, [html, fitIframe]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !offer) return null;

  const co = buildCompanyForBadge(offer.companyName, offer.companyId);
  const st = OFFER_STATUSES[offer.status];

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal offer-modal" role="dialog" aria-modal="true">
        <div className="modal-header">
          <div className="row gap-3" style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Tag size={18} strokeWidth={1.8} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                <span className="modal-title mono">{offer.ref}</span>
                {st && (
                  <span className={'badge ' + st.badge}>
                    <span className="dot" />
                    {statusLabel(OFFER_STATUSES, offer.status, lang)}
                  </span>
                )}
              </div>
              <div className="modal-sub row gap-2" style={{ flexWrap: 'wrap' }}>
                <CompanyBadge company={co} />
                <span>·</span>
                <span>
                  {isRo ? 'Emisă' : 'Issued'} {fmtDate(offer.issuedAt, lang)}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label={isRo ? 'Închide' : 'Close'}
          >
            <X size={16} />
          </button>
        </div>

        {/*
          minHeight: 0 e obligatoriu pe flex child — altfel overflow-y:auto nu scrollează
          (min-height:auto = conținutul forțează body-ul să crească peste max-height-ul modalului).
        */}
        <div className="modal-body offer-modal-body-scroll">
          {htmlLoading ? (
            <div
              className="empty"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 24px',
                minHeight: 280,
              }}
            >
              <div className="fw-600 fs-14">{isRo ? 'Se încarcă oferta…' : 'Loading offer…'}</div>
            </div>
          ) : htmlError ? (
            <div
              className="empty"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                padding: '40px 24px',
                gap: 8,
                minHeight: 280,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 99,
                  background: 'var(--st-danger-bg)',
                  color: 'var(--st-danger)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <AlertCircle size={20} strokeWidth={2} />
              </div>
              <div className="fw-600 fs-14">
                {isRo ? 'Oferta nu poate fi afișată' : 'Unable to display offer'}
              </div>
              <div className="muted fs-12" style={{ textAlign: 'center', maxWidth: 360 }}>
                {isRo
                  ? 'A apărut o eroare la randarea conținutului. Reîncercați mai târziu.'
                  : 'An error occurred while rendering the content. Please try again later.'}
              </div>
            </div>
          ) : html ? (
            // Iframe scalat pe lățime (fitIframe); scroll-ul e pe modal-body, nu pe iframe.
            <div className="offer-iframe-wrap">
              <iframe
                ref={iframeRef}
                title={`Offer ${offer.ref}`}
                srcDoc={html}
                sandbox="allow-same-origin"
                onLoad={fitIframe}
                scrolling="no"
                style={{
                  display: 'block',
                  width: '100%',
                  border: 'none',
                  background: '#ffffff',
                  transformOrigin: 'top left',
                }}
              />
            </div>
          ) : (
            <div
              className="empty"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 24px',
                minHeight: 280,
              }}
            >
              <div className="muted fs-13">
                {isRo ? 'Conținutul ofertei este indisponibil.' : 'Offer content is unavailable.'}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
